import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// Load German locale
import 'dayjs/locale/de';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

// Set default locale to German
dayjs.locale('de');

/**
 * Convert moment.js format tokens to dayjs format tokens
 * Handles differences between the two libraries
 */
function convertMomentFormat(format) {
  if (!format) return format;

  // moment.js uses 'Y' for 4-digit year, dayjs uses 'YYYY'
  // But we need to be careful not to replace 'YYYY' -> 'YYYYYYYY'
  // Only replace standalone 'Y' that's not part of 'YYYY'
  return format
    // Replace standalone Y (not followed or preceded by Y) with YYYY
    .replace(/(?<![Y])Y(?![Y])/g, 'YYYY');
}

/**
 * Vue plugin that provides moment-compatible API using dayjs
 * Replaces vue-moment with a lighter alternative
 */
export default {
  install(Vue) {
    // Add $moment to Vue prototype (compatible with vue-moment)
    Vue.prototype.$moment = dayjs;

    // Add moment filter (compatible with vue-moment filter syntax)
    Vue.filter('moment', function(value, ...args) {
      if (!value) return '';

      // Handle "from" and "now" for relative time (vue-moment syntax)
      // Example: {{ date | moment("from", "now") }}
      if (args[0] === 'from' && args[1] === 'now') {
        return dayjs(value).fromNow();
      }

      // Handle "from" with a specific date
      if (args[0] === 'from' && args[1]) {
        return dayjs(value).from(dayjs(args[1]));
      }

      // Handle simple format string
      // Example: {{ date | moment('YYYY-MM-DD') }}
      if (args.length === 1 && typeof args[0] === 'string') {
        const convertedFormat = convertMomentFormat(args[0]);
        return dayjs(value).format(convertedFormat);
      }

      // Default: return formatted date
      return dayjs(value).format();
    });
  }
};

// Also export dayjs for direct imports
export { dayjs };
